/**
 * Classify a getUserMedia / LiveKit device-acquisition failure into one of the
 * three causes the call UI surfaces. The distinction drives the affordance copy
 * (GH-1179): a *blocked* permission tells the user to re-enable access in the
 * browser settings; a *no-device* failure tells them no camera/mic was found.
 *
 * Browser reality: once a user clicks "Block" at the site level, getUserMedia
 * rejects with NotAllowedError *without re-prompting*. The app cannot re-trigger
 * the native permission dialog — so "blocked" copy points at browser settings,
 * and live recovery happens when the user grants access there and toggles again.
 */
export type MediaErrorCause = "blocked" | "no-device" | "unknown";

export function classifyMediaError(err: unknown): MediaErrorCause {
  const name =
    typeof err === "object" && err !== null && "name" in err
      ? String((err as { name?: unknown }).name)
      : "";

  switch (name) {
    // Permission denied by the user or blocked by policy/insecure context.
    case "NotAllowedError":
    case "SecurityError":
    case "PermissionDeniedError":
      return "blocked";
    // No matching hardware, or the requested constraints can't be satisfied.
    case "NotFoundError":
    case "DevicesNotFoundError":
    case "OverconstrainedError":
      return "no-device";
    // NotReadableError (device busy / hardware fault) and anything unexpected
    // fall through to a generic "couldn't access" message rather than a
    // misleading "no device detected".
    default:
      return "unknown";
  }
}
